import { useState, useEffect, useRef } from 'react'
import { ethers } from 'ethers'
import './MiningDashboard.css'
import { API_URL } from './config/api'

interface MiningDashboardProps {
  provider: any
  account: string | null
  rewardsContract: any
  language: 'zh' | 'en'
}

interface MiningStats {
  totalUsers: number
  totalDeposits: number
  totalPoints: number
}

export function MiningDashboard({
  provider,
  account,
  rewardsContract,
  language
}: MiningDashboardProps) {
  const [stats, setStats] = useState<MiningStats | null>(null)
  const [loading, setLoading] = useState(false)

  // 显示用的动画数值
  const [displayDeposits, setDisplayDeposits] = useState(0)
  const [displayPoints, setDisplayPoints] = useState(0)
  const [displayUsers, setDisplayUsers] = useState(0)

  const animationRefs = useRef<{
    deposits?: number
    points?: number
    users?: number
  }>({})

  const t = {
    zh: {
      title: '💎 积分挖矿数据',
      totalUsers: '参与用户数',
      totalDeposits: '总存款笔数',
      totalPoints: '累计积分',
      nextRank: '下一笔存款排名',
      multiplierTip: '越早参与，倍率越高！',
      earlyUser: '🔥 早期用户',
      midUser: '⭐ 中期用户',
      regularUser: '👤 常规用户',
      amountBonus: '💰 金额加成',
      tier1: '≥ 1,000 USDT: +20%',
      tier2: '≥ 5,000 USDT: +50%',
      tier3: '≥ 10,000 USDT: +100%',
      loading: '加载中...',
      currentMultiplier: '当前倍率'
    },
    en: {
      title: '💎 Points Mining Stats',
      totalUsers: 'Total Users',
      totalDeposits: 'Total Deposits',
      totalPoints: 'Total Points',
      nextRank: 'Next Deposit Rank',
      multiplierTip: 'Join early for higher multiplier!',
      earlyUser: '🔥 Early User',
      midUser: '⭐ Mid User',
      regularUser: '👤 Regular',
      amountBonus: '💰 Amount Bonus',
      tier1: '≥ 1,000 USDT: +20%',
      tier2: '≥ 5,000 USDT: +50%',
      tier3: '≥ 10,000 USDT: +100%',
      loading: 'Loading...',
      currentMultiplier: 'Current Multiplier'
    }
  }

  const content = t[language]

  // 加载积分数据
  useEffect(() => {
    loadMiningStats()
  }, [])

  const loadMiningStats = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/api/stats`)

      if (response.ok) {
        const data = await response.json()
        setStats({
          totalUsers: data.total_users,
          totalDeposits: data.total_deposits,
          totalPoints: data.total_points
        })
      } else {
        console.error('加载积分数据失败')
      }
    } catch (error) {
      console.error('加载积分数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 数字滚动动画 - Deposits
  useEffect(() => {
    if (!stats) return

    const target = stats.totalDeposits
    const current = displayDeposits

    if (Math.abs(target - current) < 0.01) {
      setDisplayDeposits(target)
      return
    }

    const duration = 1000
    const steps = 60
    const increment = (target - current) / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayDeposits(prev => prev + increment)

      if (step < steps) {
        animationRefs.current.deposits = requestAnimationFrame(animate)
      } else {
        setDisplayDeposits(target)
      }
    }

    animationRefs.current.deposits = requestAnimationFrame(animate)

    return () => {
      if (animationRefs.current.deposits) {
        cancelAnimationFrame(animationRefs.current.deposits)
      }
    }
  }, [stats?.totalDeposits])

  // 数字滚动动画 - Points
  useEffect(() => {
    if (!stats) return

    const target = stats.totalPoints
    const current = displayPoints

    if (Math.abs(target - current) < 0.01) {
      setDisplayPoints(target)
      return
    }

    const duration = 1000
    const steps = 60
    const increment = (target - current) / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayPoints(prev => prev + increment)

      if (step < steps) {
        animationRefs.current.points = requestAnimationFrame(animate)
      } else {
        setDisplayPoints(target)
      }
    }

    animationRefs.current.points = requestAnimationFrame(animate)

    return () => {
      if (animationRefs.current.points) {
        cancelAnimationFrame(animationRefs.current.points)
      }
    }
  }, [stats?.totalPoints])

  // 数字滚动动画 - Users
  useEffect(() => {
    if (!stats) return

    const target = stats.totalUsers
    const current = displayUsers

    if (Math.abs(target - current) < 0.01) {
      setDisplayUsers(target)
      return
    }

    const duration = 1000
    const steps = 60
    const increment = (target - current) / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayUsers(prev => prev + increment)

      if (step < steps) {
        animationRefs.current.users = requestAnimationFrame(animate)
      } else {
        setDisplayUsers(target)
      }
    }

    animationRefs.current.users = requestAnimationFrame(animate)

    return () => {
      if (animationRefs.current.users) {
        cancelAnimationFrame(animationRefs.current.users)
      }
    }
  }, [stats?.totalUsers])

  // 获取用户等级
  const getUserTier = (totalDeposits: number) => {
    if (totalDeposits < 1000) return { tier: content.earlyUser, color: '#fbbf24', multiplier: '3.0x' }
    if (totalDeposits < 5000) return { tier: content.midUser, color: '#8b5cf6', multiplier: '2.0x' }
    return { tier: content.regularUser, color: '#6b7280', multiplier: '1.0x' }
  }

  const userTier = stats ? getUserTier(stats.totalDeposits) : { tier: content.earlyUser, color: '#fbbf24', multiplier: '3.0x' }

  return (
    <div className="mining-dashboard">
      <h2>{content.title}</h2>

      {loading && (
        <div className="loading-state">{content.loading}</div>
      )}

      {!loading && !stats && (
        <div className="error-message">
          {language === 'zh' ? '加载数据失败，请刷新页面重试' : 'Failed to load data, please refresh'}
        </div>
      )}

      {!loading && stats && (
        <>

      {/* 统计数据网格 */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">{content.totalDeposits}</div>
          <div className="stat-value-row">
            <div className="stat-value animated">{Math.floor(displayDeposits).toLocaleString()}</div>
            <div className="stat-unit">{language === 'zh' ? '笔' : 'deposits'}</div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-label">{content.currentMultiplier}</div>
          <div className="stat-value-row">
            <div className="stat-value multiplier animated">{userTier.multiplier}</div>
          </div>
          <div className="stat-unit" style={{ color: userTier.color, fontWeight: 600 }}>
            {userTier.tier}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">{content.totalPoints}</div>
          <div className="stat-value-row">
            <div className="stat-value animated">{Math.floor(displayPoints).toLocaleString()}</div>
            <div className="stat-unit">{language === 'zh' ? '积分' : 'points'}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">{content.totalUsers}</div>
          <div className="stat-value-row">
            <div className="stat-value animated">{Math.floor(displayUsers).toLocaleString()}</div>
            <div className="stat-unit">{language === 'zh' ? '名用户' : 'users'}</div>
          </div>
        </div>
      </div>

      {/* 倍率提示 */}
      <div className="multiplier-info">
        <div className="info-icon">⚡</div>
        <div className="info-text">{content.multiplierTip}</div>
      </div>

      {/* 金额加成说明 */}
      <div className="bonus-info">
        <h3>{content.amountBonus}</h3>
        <div className="bonus-tiers">
          <div className="bonus-tier">🥉 {content.tier1}</div>
          <div className="bonus-tier">🥈 {content.tier2}</div>
          <div className="bonus-tier">🥇 {content.tier3}</div>
        </div>
      </div>

      {/* Mining Rules */}
      <div className="mining-rules">
        <h3>📋 {language === 'zh' ? '积分挖矿规则' : 'Points Mining Rules'}</h3>

        <div className="rules-grid">
          {/* 基础积分 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '💯 基础积分' : '💯 Base Points'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '• 每笔存款：100 基础积分'
                  : '• Per deposit: 100 base points'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 提款时自动计算并发放积分'
                  : '• Auto-calculated and granted on withdrawal'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 积分 = 100 × 各项加成倍率'
                  : '• Points = 100 × all bonus multipliers'}
              </p>
            </div>
          </div>

          {/* 早期用户倍率 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '⚡ 早期用户倍率' : '⚡ Early User Multiplier'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '• 前 1,000 名：3.0x 倍率 🔥'
                  : '• Top 1,000: 3.0x 🔥'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 第 1,001-5,000 名：2.0x 倍率'
                  : '• Rank 1,001-5,000: 2.0x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 第 5,000 名之后：1.0x 倍率'
                  : '• After 5,000: 1.0x'}
              </p>
              <p className="highlight-text">
                {language === 'zh'
                  ? '⚡ 排名按存款时间顺序确定！'
                  : '⚡ Rank by deposit timestamp!'}
              </p>
            </div>
          </div>

          {/* 金额加成 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '💰 金额加成' : '💰 Amount Bonus'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '🥉 ≥ 1,000 USDT: 1.2x'
                  : '🥉 ≥ 1,000 USDT: 1.2x'}
              </p>
              <p>
                {language === 'zh'
                  ? '🥈 ≥ 5,000 USDT: 1.5x'
                  : '🥈 ≥ 5,000 USDT: 1.5x'}
              </p>
              <p>
                {language === 'zh'
                  ? '🥇 ≥ 10,000 USDT: 2.0x'
                  : '🥇 ≥ 10,000 USDT: 2.0x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 小于 1,000 USDT: 1.0x（无加成）'
                  : '• Below 1,000 USDT: 1.0x (no bonus)'}
              </p>
            </div>
          </div>

          {/* 延迟提款奖励 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '⏳ 延迟提款奖励' : '⏳ Delay Bonus'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '• ≥ 1 天: 1.2x'
                  : '• ≥ 1 day: 1.2x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• ≥ 7 天: 1.5x'
                  : '• ≥ 7 days: 1.5x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• ≥ 30 天: 2.0x'
                  : '• ≥ 30 days: 2.0x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 小于 1 天: 1.0x（无加成）'
                  : '• Below 1 day: 1.0x (no bonus)'}
              </p>
            </div>
          </div>

          {/* 频率加成 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '🔄 频率加成' : '🔄 Frequency Bonus'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '• 首次存款: 1.0x'
                  : '• 1st deposit: 1.0x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 第二笔: 1.1x'
                  : '• 2nd deposit: 1.1x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 第三笔: 1.2x'
                  : '• 3rd deposit: 1.2x'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 每多一笔 +10%，上限 2.0x'
                  : '• +10% per deposit, max 2.0x'}
              </p>
            </div>
          </div>

          {/* 隐私增强 */}
          <div className="rule-section">
            <div className="rule-title">
              {language === 'zh' ? '🔐 隐私增强' : '🔐 Privacy Enhancement'}
            </div>
            <div className="rule-content">
              <p>
                {language === 'zh'
                  ? '• 混币提高匿名集大小'
                  : '• Mixing increases anonymity set'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 延迟提款增强隐私保护'
                  : '• Delays enhance privacy protection'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 多次使用提升系统安全性'
                  : '• Multiple uses improve system security'}
              </p>
              <p>
                {language === 'zh'
                  ? '• 保护隐私即获得奖励 ✨'
                  : '• Privacy protection = rewards ✨'}
              </p>
            </div>
          </div>
        </div>

        {/* 积分计算公式 */}
        <div className="mining-formula">
          <div className="formula-title">
            🧮 {language === 'zh' ? '积分计算公式' : 'Points Formula'}
          </div>
          <div className="formula-content">
            <div className="formula-box">
              {language === 'zh' ? (
                <>
                  <strong>积分</strong> = 100 × <span className="multiplier-text">早期倍率</span> × <span className="multiplier-text">金额加成</span> × <span className="multiplier-text">延迟加成</span> × <span className="multiplier-text">频率加成</span>
                </>
              ) : (
                <>
                  <strong>Points</strong> = 100 × <span className="multiplier-text">Early</span> × <span className="multiplier-text">Amount</span> × <span className="multiplier-text">Delay</span> × <span className="multiplier-text">Frequency</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 挖矿示例 */}
        <div className="mining-example">
          <div className="example-title">
            💡 {language === 'zh' ? '积分示例' : 'Example'}
          </div>
          <div className="example-content">
            {language === 'zh' ? (
              <p>
                作为<strong>前 100 名</strong>早期用户，存入 <strong>5,000 USDT</strong>，
                持有 <strong>30 天</strong>后提款，这是你的<strong>第 3 笔</strong>存款：
                <br />
                <br />
                积分 = 100 × 3.0 (早期) × 1.5 (金额) × 2.0 (延迟) × 1.2 (频率)
                <br />
                = <strong className="highlight-number">1,080</strong> 积分 ✨
              </p>
            ) : (
              <p>
                As a <strong>top 100</strong> early user, deposit <strong>5,000 USDT</strong>,
                withdraw after <strong>30 days</strong>, this is your <strong>3rd</strong> deposit:
                <br />
                <br />
                Points = 100 × 3.0 (early) × 1.5 (amount) × 2.0 (delay) × 1.2 (frequency)
                <br />
                = <strong className="highlight-number">1,080</strong> points ✨
              </p>
            )}
          </div>
        </div>

        {/* 重要提示 */}
        <div className="rules-notice">
          <div className="notice-icon">⚠️</div>
          <div className="notice-content">
            <strong>{language === 'zh' ? '重要提示' : 'Important Notice'}:</strong>
            {language === 'zh' ? (
              <>
                <span>• 积分在提款时一次性计算并发放到提款地址</span>
                <span>• 存款排名由存款事件的区块时间戳决定</span>
                <span>• 延迟时间 = 提款时间 - 存款时间</span>
                <span>• 频率加成按同一提款地址的历史存款数量计算</span>
                <span>• 积分可用于未来的代币分发、治理投票等</span>
              </>
            ) : (
              <>
                <span>• Points calculated and granted to withdrawal address</span>
                <span>• Deposit rank determined by block timestamp</span>
                <span>• Delay = withdrawal time - deposit time</span>
                <span>• Frequency based on historical deposits to same address</span>
                <span>• Points for future token distribution, governance, etc.</span>
              </>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  )
}
